import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { gbp, dateShort } from "../lib/format";
import type { ExpenseLine } from "../api/types";

/**
 * Electronic version of "Appendix 1 — Expenses Policy MISSING RECEIPT".
 * Opens from the "Missing receipt?" link on a draft line with no attachment.
 * On submit the API generates the completed declaration PDF and attaches it
 * to the line in the receipt slot.
 */
export default function MissingReceiptModal({
  requestId,
  line,
  onClose,
}: {
  requestId: string;
  line: ExpenseLine;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: api.me });

  const [vendorName, setVendorName] = useState(line.merchant_name ?? "");
  const [goods, setGoods] = useState("");
  const [printedName, setPrintedName] = useState("");
  const [department, setDepartment] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [deptTouched, setDeptTouched] = useState(false);
  const [lostTicked, setLostTicked] = useState(false);
  const [noOtherTicked, setNoOtherTicked] = useState(false);

  // Pre-fill from /me once loaded, unless the claimant already typed something.
  const effectiveName = nameTouched ? printedName : printedName || me?.name || "";
  const effectiveDept = deptTouched ? department : department || me?.entra_attrs?.department || "";

  const create = useMutation({
    mutationFn: () =>
      api.expenses.createMissingReceiptDeclaration(requestId, line.id, {
        goods_description: goods.trim(),
        vendor_name: vendorName.trim(),
        printed_name: effectiveName.trim(),
        department: effectiveDept.trim() || null,
        declared_lost: true,
        declared_no_other_claim: true,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["claim", requestId] });
      onClose();
    },
  });

  const canSubmit =
    vendorName.trim().length > 0 &&
    goods.trim().length > 0 &&
    effectiveName.trim().length > 0 &&
    lostTicked &&
    noOtherTicked &&
    !create.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" role="dialog" aria-modal="true" aria-labelledby="mr-title">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between">
          <div>
            <h2 id="mr-title" className="font-semibold text-liaise-navy">Missing receipt declaration</h2>
            <p className="text-xs text-slate-500 mt-0.5">Expenses Policy, Appendix 1</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl leading-none" aria-label="Close">×</button>
        </div>

        <div className="px-6 py-5 space-y-4 text-sm">
          <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 flex flex-wrap gap-x-6 gap-y-1 text-slate-700">
            <span><span className="text-slate-500">Date:</span> <span className="font-medium">{dateShort(line.expense_date)}</span></span>
            <span><span className="text-slate-500">Amount:</span> <span className="font-medium">{gbp(Number(line.amount))}</span></span>
            <span><span className="text-slate-500">Category:</span> <span className="font-medium">{line.category_name}</span></span>
          </div>

          <p className="text-xs text-slate-500">
            Only use this when a duplicate receipt cannot be obtained. One declaration per missing
            receipt — this is not meant to replace obtaining receipts.
          </p>

          <label className="block">
            <span className="text-slate-600 font-medium">Vendor name <span className="text-red-500">*</span></span>
            <span className="block text-xs text-slate-400">Store, hotel, airline, restaurant, etc.</span>
            <input
              type="text"
              value={vendorName}
              onChange={(e) => setVendorName(e.target.value)}
              maxLength={200}
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
            />
          </label>

          <label className="block">
            <span className="text-slate-600 font-medium">Detailed list of the goods or services purchased <span className="text-red-500">*</span></span>
            <textarea
              value={goods}
              onChange={(e) => setGoods(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="e.g. Lunch for 2 people we support during activity — sandwiches and drinks"
              className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
            />
          </label>

          <div className="space-y-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            <label className="flex items-start gap-2.5">
              <input type="checkbox" checked={lostTicked} onChange={(e) => setLostTicked(e.target.checked)} className="mt-0.5" />
              <span className="text-slate-700">
                I hereby declare that I have lost or accidentally destroyed the original receipt.
              </span>
            </label>
            <label className="flex items-start gap-2.5">
              <input type="checkbox" checked={noOtherTicked} onChange={(e) => setNoOtherTicked(e.target.checked)} className="mt-0.5" />
              <span className="text-slate-700">
                I further declare that I have not and will not use this receipt (if found) to claim
                reimbursement from any other source, or to support any claim for income tax
                deductions in the future.
              </span>
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-slate-600 font-medium">Printed name <span className="text-red-500">*</span></span>
              <input
                type="text"
                value={effectiveName}
                onChange={(e) => { setNameTouched(true); setPrintedName(e.target.value); }}
                maxLength={200}
                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
              />
            </label>
            <label className="block">
              <span className="text-slate-600 font-medium">Department</span>
              <input
                type="text"
                value={effectiveDept}
                onChange={(e) => { setDeptTouched(true); setDepartment(e.target.value); }}
                maxLength={200}
                className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2"
              />
            </label>
          </div>

          <p className="text-xs text-slate-500">
            Submitting signs this declaration electronically under your account and attaches the
            completed form to this line in place of the receipt.
          </p>

          {create.error && (
            <div className="text-sm text-red-600">{(create.error as Error).message}</div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-3">
          <button onClick={onClose} className="text-slate-600 px-4 py-2 hover:underline">Cancel</button>
          <button
            onClick={() => create.mutate()}
            disabled={!canSubmit}
            className="bg-liaise-navy text-white font-medium px-5 py-2.5 rounded-lg hover:bg-liaise-navy-700 disabled:opacity-50 transition"
          >
            {create.isPending ? "Attaching…" : "Sign & attach declaration"}
          </button>
        </div>
      </div>
    </div>
  );
}
